/**
 * THE THROTTLE ON EVERY "SEND IT AGAIN" BUTTON
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY IT IS CLIENT-SIDE, AND WHY IT STARTS ON THE ATTEMPT RATHER THAN ON
 * THE SUCCESS
 * ─────────────────────────────────────────────────────────────────────────
 *
 * GoTrue refuses a second mail to the same address inside `SMTP_MAX_FREQUENCY`, which is
 * unset in every Lumen deployment and therefore the built-in 60 seconds. The reset screen's
 * "Send it again" had no throttle at all: pressing it twice produced a 429, which
 * `onForgot` logged with `console.warn` and then showed the "Check your inbox" screen
 * anyway — the product claiming to have sent an email that GoTrue had just refused to send.
 *
 * THE COOLDOWN STARTS WHEN THE REQUEST IS MADE, NOT WHEN IT SUCCEEDS, and that is a
 * privacy decision rather than a convenience. GoTrue's per-address frequency limit only
 * bites for an address that EXISTS — an unknown address is answered 200 with no mail sent.
 * A countdown that appeared only after a real 429 would therefore be an account-enumeration
 * oracle: type an address, see whether the timer appears. Starting it on the attempt makes
 * the screen behave identically for an address that exists and one that does not, which is
 * the same reason the "sent" screen is shown either way.
 *
 * Held in memory rather than in storage: this is a UI throttle, and GoTrue's own 429 is the
 * enforcement. A reload releases it, and the 429 that may follow is folded back into the
 * cooldown by `absorbRateLimit()` instead of being shown to the user.
 */

/** GoTrue's `SMTP_MAX_FREQUENCY` default, which no deployment overrides. */
export const RESEND_COOLDOWN_SECONDS = 60;

/** key → epoch ms at which the next send is allowed. */
const until = new Map<string, number>();

/**
 * Both keys live here so the two senders cannot disagree about what they are throttling.
 *
 * The address is lower-cased because GoTrue does: `a@x.com` and `A@x.com` are one account
 * and share one send budget, so keying on the typed casing would hand the user a second
 * button that the server refuses.
 */
export function emailResendKey(email: string): string {
  return `email:${email.trim().toLowerCase()}`;
}

/**
 * Keyed on the user id, because that is all the re-invite call site has.
 *
 * A consequence worth naming: an administrator re-inviting someone and that person asking
 * for a password reset are two keys against one server-side budget, so the second of the
 * two can still meet a 429. That is absorbed rather than shown.
 */
export function reinviteKey(userId: string): string {
  return `reinvite:${userId}`;
}

/** Seconds left, rounded UP so a countdown never shows 0 while the button is still dead. */
export function cooldownRemaining(key: string, now: number = Date.now()): number {
  const at = until.get(key);
  if (at === undefined) return 0;
  return Math.max(0, Math.ceil((at - now) / 1000));
}

export function startCooldown(
  key: string,
  seconds: number = RESEND_COOLDOWN_SECONDS,
  now: number = Date.now(),
): void {
  /*
   * Never shortened. `absorbRateLimit` can be handed a longer wait than the default — the
   * address may have been mailed from another tab, or by an administrator's re-invite —
   * and taking the max is what stops the second call from handing the user a button that
   * is about to fail again.
   */
  const next = now + seconds * 1000;
  until.set(key, Math.max(until.get(key) ?? 0, next));
}

/**
 * Release a cooldown that was started for a request which never reached the server.
 *
 * A DNS failure or a dead gateway means no mail was sent, so holding the user for a minute
 * punishes them for the stack being down — and the retry they will obviously make is the
 * correct thing for them to do.
 */
export function releaseCooldown(key: string): void {
  until.delete(key);
}

/** Tests only — the map is module state and would otherwise leak between cases. */
export function resetCooldowns(): void {
  until.clear();
}

/**
 * GoTrue words its 429 as "For security purposes, you can only request this after 41
 * seconds." Reading the number back means the countdown matches the server's opinion rather
 * than restarting a fresh minute the server will not honour.
 */
export function parseRetryAfterSeconds(message: string): number | null {
  const m = /after (\d+) second/i.exec(message);
  if (!m?.[1]) return null;
  const n = Number(m[1]);
  /* An hour is `RATE_LIMIT_EMAIL_SENT`, not the frequency limit; anything past that is a
     misread and a wrong countdown is worse than a default one. */
  return Number.isFinite(n) && n > 0 && n <= 3600 ? n : null;
}

/**
 * Fold a rate-limit refusal back into the cooldown.
 *
 * Returns true when the error WAS a rate limit, so the caller knows not to show it: the
 * user asked for an email, an email had already been sent, and "check your inbox" is both
 * the truth and the non-enumerating answer.
 */
export function absorbRateLimit(key: string, status: number | undefined, message: string, now: number = Date.now()): boolean {
  const limited = status === 429 || /rate limit|too many|only request this after/i.test(message);
  if (!limited) return false;
  startCooldown(key, parseRetryAfterSeconds(message) ?? RESEND_COOLDOWN_SECONDS, now);
  return true;
}
